// The canonical one-line activity row from the gallery's check-in section:
// a small fixed-width glyph column beside quiet `text-v3-chat-meta` narration text. Most
// of the 42 event renderers reduce to this shape, so its metrics live here
// once. Outcome counts inside the text use EventLineStat tones.
// The row starts at the transcript's left edge, full width, since it is not
// part of a message; it aligns to the top so a wrapped line keeps its glyph
// on the first line.
import { Fragment, type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';

export type EventLineProps = Readonly<{
    readonly glyph: ReactNode;
    readonly children: ReactNode;
    readonly className?: string;
    readonly onClick?: () => void;
    readonly expanded?: boolean;
}>;

export function EventLine({
    glyph,
    children,
    className,
    onClick,
    expanded,
}: EventLineProps): ReactNode {
    const lineClassName = cn(
        'flex w-full items-start gap-1.5',
        'text-muted-foreground text-v3-chat-meta',
        className,
    );
    const body = (
        <Fragment>
            <span
                aria-hidden="true"
                className="text-foreground/75 flex h-[1lh] w-3.5 shrink-0 items-center justify-center [&_svg]:size-[1em]"
            >
                {glyph}
            </span>
            <span className="min-w-0">{children}</span>
        </Fragment>
    );
    if (onClick !== undefined) {
        return (
            <button
                type="button"
                aria-expanded={expanded}
                onClick={onClick}
                className={cn(lineClassName, 'hover:text-foreground text-left')}
            >
                {body}
            </button>
        );
    }
    return <div className={lineClassName}>{body}</div>;
}

export type EventLineStatTone = 'positive' | 'negative';

export type EventLineStatProps = Readonly<{
    readonly tone: EventLineStatTone;
    readonly children: ReactNode;
}>;

export function EventLineStat({
    tone,
    children,
}: EventLineStatProps): ReactNode {
    switch (tone) {
        case 'positive':
            return (
                <span className="text-agent-positive font-semibold">
                    {children}
                </span>
            );
        case 'negative':
            return (
                <span className="text-agent-negative font-semibold">
                    {children}
                </span>
            );
        default: {
            const exhaustiveTone: never = tone;
            return exhaustiveTone;
        }
    }
}
